import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors',
        // Pravos Design System Buttons
        'pravos-primary':
          'relative isolate bg-button-gradient text-white uppercase tracking-[0.27em] py-6 px-10 rounded-button hover:scale-[1.02] transition-all duration-300 shadow-glow-md hover:shadow-glow-lg',
        'pravos-secondary':
          'bg-glass-dark border-2 border-glass-border shadow-glass-inset text-text-primary hover:bg-white/5 uppercase tracking-[5.4px] py-6 px-10 rounded-button hover:scale-[1.02] transition-all duration-300',
        'pravos-highlight':
          'relative isolate bg-button-gradient-secondary text-white uppercase tracking-[0.27em] py-6 px-10 rounded-button hover:scale-[1.02] transition-all duration-300 shadow-glow-md hover:shadow-glow-lg',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 transition-colors',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 transition-colors',
        ghost: 'hover:bg-accent hover:text-accent-foreground transition-colors',
        link: 'text-primary underline-offset-4 hover:underline transition-colors',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
